import { Link as RouterLink } from 'react-router-dom';
import { visit } from 'unist-util-visit';

import {
  getRouteSellerIntegrationsInboundDocsPage,
  getRouteSellerIntegrationsOverviewPage,
  getRouteSellerIntegrationsSellerConfigPage,
  getRouteSellerIntegrationsSellerDocsPage,
  getRouteSellerIntegrationsSellerReqsPage,
} from 'shared/constants/routerConst';

// Scrolls to an anchor if it is present in the URL
// Used for deep linking to specific sections of the page linked from markdown
const scrollToAnchor = () => {
  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (hash) {
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }
};

// These are all of the routes we want to be able to dynamically link via markdown
// It's just special because we need to be able to pass a param to the route
const markdownRouteMap: Record<string, (id: string) => string> = {
  overviewLink: getRouteSellerIntegrationsOverviewPage,
  inboundDocsLink: getRouteSellerIntegrationsInboundDocsPage,
  sellerDocsLink: getRouteSellerIntegrationsSellerDocsPage,
  sellerReqsLink: getRouteSellerIntegrationsSellerReqsPage,
  sellerConfigLink: getRouteSellerIntegrationsSellerConfigPage,
};

// A remark directive plugin that adds support for custom behaviors via markdown annotations
const markdownDirectivePlugin = () => {
  return (tree: any) => {
    visit(tree, (node) => {
      // Add support for indenting content via markdown
      // Example:
      // :::indent
      // Content here
      // :::
      if (
        (node.type === 'textDirective' || node.type === 'containerDirective') &&
        node.name === 'indent'
      ) {
        node.data = {
          hName: 'div',
          hProperties: {
            style: { marginLeft: '40px' },
          },
        };
      }

      // Add support for internal React links via markdown
      // Basic Example: :ReactLink[]{path="sellerReqsLink" text="Integration Details"}
      // Anchor Example: :ReactLink[]{path="sellerReqsLink" text="Integration Details" anchor="product-catalog-import"}
      if (
        (node.type === 'textDirective' || node.type === 'leafDirective') &&
        node.name === 'ReactLink'
      ) {
        node.data = {
          hName: node.name,
          hProperties: {
            children: node.attributes?.text,
            path: node.attributes?.path,
            anchor: node.attributes?.anchor,
          },
        };
      }
    });
  };
};

// Used with custom directive links to support dynamic routing (see markdownRouteMap)
// You can use this without dynamic links, but you don't need to thanks to the `MarkdownLink` component
// See comment above for examples of usage of the directive
const createReactLink = (param: string) => {
  return ({ node }: any) => {
    const { children, anchor, path } = node.properties ?? {};
    const baseUrl = markdownRouteMap[path]?.(param) ?? path;
    const to = anchor ? `${baseUrl}#${anchor}` : baseUrl;
    return <RouterLink to={to}>{children}</RouterLink>;
  };
};

// Used to style the TOC plugin's output
// There really isn't a better way I found to do this without replacing the TOC plugin with our own
// Feels easier to just manipulate it after the fact
const rehypeTocStyler = () => {
  return (tree: any) => {
    visit(tree, 'element', (node: any, index, parent) => {
      if (
        node.tagName === 'ul' &&
        parent?.children?.[index - 2]?.tagName === 'h2' &&
        parent?.children?.[index - 2]?.children?.[0]?.value ===
          'Table of Contents'
      ) {
        // Add a class to the TOC ul for styling purposes
        node.properties = {
          ...(node.properties || {}),
          className: ['markdown-toc'],
        };
      }
    });
  };
};

// Render a tags with a special MarkdownLink component that bounces between React router links and regular links
const MarkdownLink = ({ href, children, ...props }: any) => {
  if (href && (href.startsWith('/') || href.startsWith('#'))) {
    return (
      <RouterLink to={href} {...props}>
        {children}
      </RouterLink>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

export {
  MarkdownLink,
  createReactLink,
  markdownDirectivePlugin,
  markdownRouteMap,
  rehypeTocStyler,
  scrollToAnchor,
};
